import type { FC, PropsWithChildren } from 'react';
import type { RuleSet } from 'styled-components';

import { useMemo } from 'react';
import styled, { css } from 'styled-components';

import { media } from '~/components/shared/core/styles';
import { useTileBaseContext } from '~/components/shared/recommendations/tile/TileBaseContext';
import { useTileContext } from '~/components/shared/recommendations/tile/TileContext';
import { useStaticContext } from '~/utilities/context/static/StaticContext';
import { LowestPriceDisplay, SiteType } from '~/utilities/graphql/codegen';
import { parseFormattedPrice } from '~/utilities/helpers/parseFormattedPrice';

const WahtsWrapperStyles = css`
  color: ${({ theme }) => theme.colors.content.tertiary};
  font-size: ${({ theme }) => theme.font.size[2]};
  font-weight: ${({ theme }) => theme.variables.font.weight.regular};
  line-height: 1.4;
`;

const WrapperStyles: Record<SiteType, RuleSet<object>> = {
  [SiteType.Gstar]: css``,
  [SiteType.Aaf]: css``,
  [SiteType.Employeeshop]: css``,
  [SiteType.Outlet]: css``,
  [SiteType.Wahts]: WahtsWrapperStyles,
};

const S = {
  Wrapper: styled.div`
    color: ${({ theme }) => theme.colors.content.tertiary};
    font-size: ${({ theme }) => theme.font.size['2']};
    line-height: 1.4;
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding-top: 2px;
    min-height: 17px; /* min-height for 1 lines of lowest 30 days price */

    &:where([data-hover-variant-area='true']:hover &),
    &:where([data-hover-variant-area='true']:focus-within:not(:focus:not(:focus-visible)) &) {
      @media ${({ theme }) => media(theme).greaterThan('lg')} {
        line-height: 1.2;
      }
    }

    ${({ theme }) => WrapperStyles[theme.siteType]}
  `,

  LowestPrice: styled.span`
    white-space: nowrap;
  `,
};

interface ComponentProps extends PropsWithChildren {
  readonly?: boolean;
  flexible?: boolean;
}

export const TilePriceLowest30Days: FC<ComponentProps> = ({ children, readonly, flexible }) => {
  const {
    configuration: { lowestPriceDisplay },
    priceFormatter,
  } = useStaticContext();
  const { singleProduct } = useTileBaseContext();
  const { product, productPrice } = useTileContext();

  const currentProduct = readonly ? product : singleProduct;
  const lowest30PriceCopy = productPrice?.lowestPrice30days;

  const [initialPrice, lowestPrice] = useMemo(
    () => [
      priceFormatter(currentProduct.fromPriceValue),
      currentProduct?.lowestPrice ? priceFormatter(currentProduct?.lowestPrice) : '',
    ],
    [currentProduct, priceFormatter]
  );

  const parsedLowestPrice = parseFormattedPrice(lowestPrice);
  const parsedInitialPrice = parseFormattedPrice(initialPrice);

  const isRRPLast30Days = parsedInitialPrice === parsedLowestPrice;
  const allowedLowestPriceDisplay = useMemo(
    () =>
      lowestPriceDisplay &&
      [
        LowestPriceDisplay.Always,
        LowestPriceDisplay.AlwaysDiscountsOnly,
        LowestPriceDisplay.OnlyLowestPrice,
      ].includes(lowestPriceDisplay),
    [lowestPriceDisplay]
  );

  const isDisplayedLowest30Days =
    (!isRRPLast30Days || allowedLowestPriceDisplay) && lowest30PriceCopy && lowestPrice !== '';

  if (!isDisplayedLowest30Days && flexible) {
    return children;
  }

  return (
    <S.Wrapper>
      {isDisplayedLowest30Days ? (
        <div data-testid="product-tile-lowest-price">
          {`${lowest30PriceCopy} `}
          <S.LowestPrice>{lowestPrice}</S.LowestPrice>
        </div>
      ) : (
        children
      )}
    </S.Wrapper>
  );
};
